export type UnheldRules = Record<string, unknown>;

type Json = Record<string, unknown>;

const isObject = (value: unknown): value is Json =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const SECTION_NAMES = [
  "fixture",
  "range",
  "merchandising",
  "high_density_mode",
  "high_density_building_blocks_mode",
  "relax_factors",
  "mudmap_generation_penalty_multiplier",
] as const;
type Section = (typeof SECTION_NAMES)[number];
const SECTIONS = new Set<string>(SECTION_NAMES);

export type RulesOwnership<T, O> = {
  [K in keyof T]-?: K extends Section ? { [P in keyof NonNullable<T[K]>]-?: true } : boolean;
} & {
  [K in keyof O]: K extends keyof T
    ? K extends Section
      ? { [P in keyof O[K]]: P extends keyof NonNullable<T[K]> ? true : never }
      : boolean
    : never;
};

type Ownership = Record<string, boolean | Record<string, true>>;
export const rulesOwnership = {
  fixture: {
    can_adjust_shelf_notches: true,
    can_add_or_delete_shelves: true,
    shelves_same_height_as_base_pog: true,
    vertical_overlap: true,
    merged_shelf_width_tolerance_pct: true,
    vertical_split_section: true,
    finger_space: true,
  },
  range: {
    can_add_non_core_range_products: true,
    can_apply_squeeze_to_placed_products: true,
    min_candidate_quantity: true,
  },
  cdt_source: true,
  merchandising: {
    vertical_blocking_mode: true,
    stack_to_shelf_top: true,
    vertical_capping: true,
    stack_past_max_stack: true,
    vertical_capping_past_max_stack: true,
    can_break_cases_trays_if_core_range_not_fit: true,
    can_fill_extra_space_next_to_cases_trays_with_loose_products: true,
    how_should_loose_products_be_placed_with_respect_to_their_cases_trays: true,
    leverage_alternative_orientations: true,
    extra_facing_marginal_value: true,
    snaking: true,
    snaking_cascade_cdt_level: true,
    cdt_stacking: true,
    items_squeeze_option: true,
    dividers: true,
  },
  high_density_mode: {
    enabled: true,
    place_all_variants: true,
    snaking_strategy: true,
    variant_contiguous: true,
    allow_breaking_variant_sequencing: true,
    vertical_blocking_mode: true,
    strict_rectangles: true,
    brand_blocks_min_bays: true,
  },
  high_density_building_blocks_mode: { enabled: true, direction: true, start_from: true },
  mudmap_level: true,
  optimize_on: true,
  relax_factors: {
    space_mix: true,
    either_or: true,
    both_or_none: true,
    prereq: true,
    shelf_item_count: true,
    fixed_notch_num: true,
    finger_space: true,
    units_not_enough_dos: true,
    units_not_enough_mos: true,
    units_too_many: true,
    facings_not_enough: true,
    facings_too_many: true,
    rows_not_enough: true,
    min_width: true,
    min_distribution: true,
    width_slack: true,
    cdt1_contiguous: true,
    cdt2_contiguous: true,
    cdt3_contiguous: true,
    cdt1_order: true,
    cdt2_order: true,
    cdt3_order: true,
    item_id_order: true,
    item_span: true,
    cdt1_span: true,
    cdt2_span: true,
    cdt3_span: true,
    secondary_orientation: true,
    shelf_segment_space: true,
  },
  mudmap_generation_penalty_multiplier: {
    totalSpaceSlack: true,
    cdt0SectionsSpanning: true,
    cdt0ShelvesSpanning: true,
    cdt2SectionsSpanning: true,
    cdt2ShelvesSpanning: true,
    cdt0RoundingSlack: true,
    cdt2RoundingSlack: true,
    cdt1BreakingSequencing: true,
    cdt2BreakingSequencing: true,
    cdtSpaceMixSlack: true,
    cdt0ShelvesMisaligned: true,
    itemWidthSatisfied: true,
    cdt0ShelfSpaceMisalignment: true,
    cdt2ShelfSpaceMisalignment: true,
    verticalBlockShelfPresence: true,
    cdt0CellXMisalignment: true,
    cdt2CellXMisalignment: true,
  },
  // Base mode overrides this: imported pallets have no base form and remain unheld.
  pallets: true,
  weight_rules: true,
} as const;

export const baseRulesOwnership = {
  ...rulesOwnership,
  pallets: false,
  mudmap_generation_penalty_multiplier: true,
} as const;

export const unheldOf = (rules: object, ownership: Ownership): UnheldRules => {
  const mine = ownership;
  const unheld: UnheldRules = {};
  for (const [key, value] of Object.entries(rules)) {
    if (!(key in mine) || mine[key] === false) {
      unheld[key] = value;
      continue;
    }
    const section = mine[key];
    if (!SECTIONS.has(key) || !isObject(value) || !isObject(section)) continue;
    const rest = Object.entries(value).filter(([inner]) => !(inner in section));
    if (rest.length > 0) unheld[key] = Object.fromEntries(rest);
  }
  return unheld;
};

export const withUnheld = <T extends object>(held: T, unheld: UnheldRules): T => {
  const rules: Json = { ...unheld, ...held };
  for (const [key, value] of Object.entries(unheld)) {
    const section = (held as Json)[key];
    if (SECTIONS.has(key) && isObject(value) && isObject(section)) {
      rules[key] = { ...value, ...section };
    }
  }
  return rules as T;
};

const isSame = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

// O must not read unchanged defaults added by the modal as user changes.
export const withoutAddedDefaults = <T extends object>(
  rules: T,
  given: object,
  built: object,
): T => {
  const strip = (now: Json, had: Json, first: Json): Json =>
    Object.fromEntries(
      Object.entries(now).flatMap(([key, value]): [string, unknown][] => {
        if (!(key in had)) return isSame(value, first[key]) ? [] : [[key, value]];
        const [before, was] = [had[key], first[key]];
        return isObject(value) && isObject(before) && isObject(was)
          ? [[key, strip(value, before, was)]]
          : [[key, value]];
      }),
    );
  return strip(rules as Json, given as Json, built as Json) as T;
};
